@if(loading) {
  <div class="fixed inset-0 bg-opacity-50 backdrop-blur-sm flex items-center justify-center" style="z-index: 999"
    role="status" aria-live="polite" aria-label="Loading">
    <mat-progress-spinner class="!h-[50px] !w-[50px]" mode="indeterminate" aria-label="Loading" />
  </div>
}

@if(resetForm === false && !showOtpInput) {
  <section class="max-sm:p-[4rem] sm:w-[51.2rem] flex flex-col items-center justify-center" role="dialog"
    aria-labelledby="login-title" aria-modal="true">
    <div class="sm:w-[39.4rem] flex flex-col items-center justify-center sm:gap-[2rem] gap-[5rem]">
      <button class="absolute top-[4rem] right-[4rem]" (click)="closePopup()" type="button"
        aria-label="Close login dialog">
        <dsr-icon name="cancel" size="1.6rem" color="black" aria-hidden="true"></dsr-icon>
      </button>
      <header class="flex flex-col items-center justify-center gap-2">
        <h2 mat-dialog-title id="login-title" class="!min-h-fit">
          {{ "FORM_LOGIN_BUTTON" | translate }}
        </h2>
        <p class="dsr-typescale-body-small-prominent text-start">
          *{{ "FORM_MANDATORY_TEXT" | translate }}
        </p>
      </header>
      <form [formGroup]="loginForm" (ngSubmit)="login()" class="w-full">
        <fieldset class="w-full min-w-full flex flex-col sm:gap-[2rem] gap-[4rem] h-full items-center border-none">
          <legend class="sr-only">Login credentials</legend>
          <mat-dialog-content class="!max-h-none !m-0 !p-0 w-full">
            <div class="w-full flex flex-col gap-[2.5rem] pt-2">
              <mat-form-field appearance="outline" class="w-full">
                <mat-label>E-mail</mat-label>
                <input matInput id="login-email" type="email" formControlName="email" required
                  [placeholder]="'FORM_EMAIL_PLACEHOLDER' | translate" aria-required="true"
                  [attr.aria-invalid]="loginForm.get('email')?.invalid && loginForm.get('email')?.touched" 
                  [attr.aria-describedby]="getErrorMessage('email') ? 'email-error' : null" />
                <dsr-icon name="mail" matSuffix aria-hidden="true"></dsr-icon>
                @if(loginForm.get("email")?.invalid && loginForm.get("email")?.touched) {
                  <mat-error id="email-error">{{ getErrorMessage("email") }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
                <mat-label>{{ "FORM_PASSWORD_LABEL" | translate }}</mat-label>
                <input matInput id="login-password" type="password" formControlName="password" aria-required="true"
                  [attr.aria-invalid]="loginForm.get('password')?.invalid && loginForm.get('password')?.touched" 
                  [attr.aria-describedby]="getErrorMessage('password') ? 'password-error' : null" required />
                <dsr-icon name="lock" matSuffix aria-hidden="true"></dsr-icon>
                @if(loginForm.get("password")?.invalid && loginForm.get("password")?.touched) {
                  <mat-error id="password-error">{{ getErrorMessage("password") }}</mat-error>
                }
              </mat-form-field>
            </div>
            <!-- Champ d'erreur d'authentification -->
            @if(loginError) {
              <div class="w-full mt-4 p-3 bg-red-50 border border-red-200 rounded-md" role="alert" aria-live="assertive">
                <span class="text-red-600 dsr-typescale-body-small">
                  {{ loginError }}
                </span>
              </div>
            }
            <div>
              <div class="flex items-center justify-end">
                <button type="button" (click)="navigateToForgotPassword(true)" class="underline cursor-pointer"
                  aria-label="Forgot password">
                  {{ "LOGIN_FORGOT_PASSWORD_LABEL" | translate }}
                </button>
              </div>
            </div>
          </mat-dialog-content>
          <mat-dialog-actions class="">
            <button mat-flat-button class="" type="submit" [disabled]="loading || loginForm.invalid"
              [attr.aria-busy]="loading">
              <span class="">{{ "FORM_LOGIN_BUTTON" | translate }}</span>
            </button>
            <button mat-stroked-button class="" color="primary" type="button" (click)="navigateToRegistration()"
              aria-label="Register for a new account">
              <span class="">{{ "FORM_REGISTER_BUTTON" | translate }}</span>
            </button>
          </mat-dialog-actions>
        </fieldset>
      </form>
    </div>
  </section>
}

<!-- OTP Verification Section -->
@if(showOtpInput) {
  <section class="sm:h-[45rem] max-sm:p-[4rem] sm:w-[51.2rem] flex flex-col items-center justify-center" role="dialog"
    aria-labelledby="otp-title" aria-modal="true">
    <div class="sm:h-[33rem] min-h-fit sm:w-[39.4rem] flex flex-col items-center justify-center sm:gap-[2rem] gap-[5rem]">
      <button class="absolute top-[4rem] right-[4rem] cursor-pointer" (click)="cancelOtp()" type="button"
        aria-label="Cancel OTP entry">
        <dsr-icon name="cancel" size="1.6rem" color="black" aria-hidden="true"></dsr-icon>
      </button>
      <header class="flex flex-col items-center justify-center gap-2 text-center">
        <h2 mat-dialog-title id="otp-title" class="!min-h-fit">
          {{ "LOGIN_OTP_TITLE" | translate }}
        </h2>
        <p class="dsr-typescale-body-small-prominent">
          {{ "LOGIN_OTP_DESCRIPTION" | translate }}
        </p>
        <p class="dsr-typescale-body-small-prominent">
          *{{ "FORM_MANDATORY_TEXT" | translate }}
        </p>
      </header>
      <form [formGroup]="otpForm" (ngSubmit)="verifyOtp()" class="w-full">
        <fieldset class="">
          <legend class="sr-only">OTP verification</legend>
          <mat-dialog-content class="!max-h-none !m-0 !p-0 w-full">
            <div class="w-full flex flex-col gap-[2.5rem] pt-2">
              <mat-form-field appearance="outline" class="w-full">
                <mat-label>{{ "LOGIN_OTP_LABEL" | translate }}</mat-label>
                <input matInput id="otp-code" type="text" formControlName="otpCode" maxlength="6" required
                  [placeholder]="'LOGIN_OTP_PLACEHOLDER' | translate" autocomplete="one-time-code"
                  [attr.aria-invalid]="otpForm.get('otpCode')?.invalid && otpForm.get('otpCode')?.touched"
                  [attr.aria-describedby]="getOtpErrorMessage() ? 'otp-error' : null" aria-required="true" />
                @if(otpForm.get("otpCode")?.invalid && otpForm.get("otpCode")?.touched) {
                  <mat-error id="otp-error">{{ getOtpErrorMessage() }}</mat-error>
                }
              </mat-form-field>
            </div>
            <!-- OTP Error Message -->
            @if(otpError) {
              <div class="w-full mt-4 p-3 bg-red-50 border border-red-200 rounded-md" role="alert" aria-live="assertive">
                <span class="text-red-600 dsr-typescale-body-small">
                  {{ otpError }}
                </span>
              </div>
            }
          </mat-dialog-content>
          <mat-dialog-actions class="">
            <button mat-flat-button class="" type="submit" [disabled]="loading || otpForm.invalid"
              [attr.aria-busy]="loading">
              <span class="">{{ "LOGIN_OTP_VERIFY" | translate }}</span>
            </button>
            <button mat-stroked-button class="" color="primary" type="button" (click)="cancelOtp()"
              aria-label="Cancel and return to login">
              <span class="!capitalize">{{ "BUTTON_CANCEL" | translate }}</span>
            </button>
          </mat-dialog-actions>
        </fieldset>
      </form>
    </div>
  </section>
}

@if(resetForm === true) {
  <section class="max-sm:p-[4rem] sm:w-[51.2rem] flex flex-col items-center justify-center" role="dialog"
    aria-labelledby="reset-password-title" aria-modal="true">
    <div class="sm:w-[39.4rem] flex flex-col items-center justify-center sm:gap-[2rem] gap-[5rem]">
      <button class="absolute top-[4rem] right-[4rem] cursor-pointer" (click)="closePopup()" type="button"
        aria-label="Close reset password dialog">
        <dsr-icon name="cancel" size="1.6rem" color="black" aria-hidden="true"></dsr-icon>
      </button>
      <header class="flex flex-col items-center justify-center w-full gap-2 text-center">
        <h2 mat-dialog-title id="reset-password-title" class="!min-h-fit">
          {{ "LOGIN_RESET_BUTTON" | translate }}
        </h2>
        <p class="dsr-typescale-body-small-prominent">
          *{{ "FORM_MANDATORY_TEXT" | translate }}
        </p>
      </header>
      <form [formGroup]="forgotPasswordForm" class="w-full">
        <fieldset class="">
          <legend class="sr-only">Reset password form</legend>
          <mat-dialog-content class="!max-h-none !m-0 !p-0 w-full">
            <div class="w-full flex flex-col gap-[4.2rem] pt-2">
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
                <mat-label>E-mail</mat-label>
                <input matInput id="reset-email" type="email" formControlName="resetEmail" required
                  [(ngModel)]="resetUserMail" [placeholder]="'FORM_EMAIL_PLACEHOLDER' | translate" 
                  [attr.aria-invalid]="forgotPasswordForm.get('resetEmail')?.invalid && forgotPasswordForm.get('resetEmail')?.touched" 
                  [attr.aria-describedby]="getResetEmailErrorMessage() ? 'reset-email-error' : null" aria-required="true" />
                <dsr-icon name="mail" matSuffix aria-hidden="true"></dsr-icon>
                @if(forgotPasswordForm.get("resetEmail")?.invalid && forgotPasswordForm.get("resetEmail")?.touched) {
                  <mat-error id="reset-email-error">{{ getResetEmailErrorMessage() }}</mat-error>
                }
              </mat-form-field>
            </div>
            @if(forgotPasswordError) {
              <div class="w-full mt-4 p-3 text-center bg-red-50 border border-red-200 rounded-md" role="alert"
                aria-live="assertive">
                <span class="text-red-600 text-center dsr-typescale-body-small">
                  {{ forgotPasswordError }}
                </span>
              </div>
            }
            @if(successMessage) {
              <div class="w-full mt-4 p-3 text-center bg-green-50 border border-green-200 rounded-md" role="status"
                aria-live="polite">
                <span class="text-black-600 text-center dsr-typescale-body-small">
                  {{ successMessage }}
                </span>
              </div>
            }
            <div class="flex w-full justify-end">
              <button type="button" (click)="navigateToForgotPassword(false)" [disabled]="loading"
                class="underline cursor-pointer" aria-label="Login">
                {{ "LOGIN_HEADER_TITLE" | translate }}
              </button>
            </div>
          </mat-dialog-content>
          <mat-dialog-actions class="">
            <button mat-flat-button class="" type="submit" (click)="forgotPassword()"
              [disabled]="loading || forgotPasswordForm.invalid" [attr.aria-busy]="loading" aria-label="Reset password">
              <span class="">{{ "LOGIN_RESET_BUTTON" | translate }}</span>
            </button>
            <button mat-stroked-button color="primary" class="" type="button" (click)="closePopup()" aria-label="Cancel">
              <span class="!capitalize">{{ "CANCEL_BUTTON" | translate }}</span>
            </button>
          </mat-dialog-actions>
        </fieldset>
      </form>
    </div>
  </section>
}